import { useEffect, useState } from 'react'
import { Accordion, Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { useAuth } from '../../../../modules/auth'
import { parseId } from '../../../helpers'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { useGetUser } from '../../../store/userStore'
import { IAllUserNode } from '../../../types/users'
import {
  ChannelConfigData,
  TelephonyProviderValue,
  VicidialCampaignOption,
  fetchVicidialCampaignOptions,
  useChannelConfig,
  useUserChannelConfig,
} from '../../channelConfig/hooks/useChannelConfig'

interface Props {
  node: IAllUserNode
}

type UserTelephonyForm = Pick<
  ChannelConfigData,
  | 'telephony_provider'
  | 'vicidial_enabled'
  | 'vicidial_agent_user'
  | 'vicidial_agent_password'
  | 'vicidial_phone_login'
  | 'vicidial_phone_password'
  | 'vicidial_campaign_id'
>

const EMPTY_USER_TELEPHONY_FORM: UserTelephonyForm = {
  telephony_provider: '',
  vicidial_enabled: false,
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_campaign_id: '',
}

const buildUserTelephonyForm = (config?: Partial<ChannelConfigData>): UserTelephonyForm => ({
  telephony_provider: (config?.telephony_provider as TelephonyProviderValue) || '',
  vicidial_enabled: Boolean(config?.vicidial_enabled),
  vicidial_agent_user: config?.vicidial_agent_user || '',
  vicidial_agent_password: config?.vicidial_agent_password || '',
  vicidial_phone_login: config?.vicidial_phone_login || '',
  vicidial_phone_password: config?.vicidial_phone_password || '',
  vicidial_campaign_id: config?.vicidial_campaign_id || '',
})

export const UserChannelConfigTab: React.FC<Props> = ({ node }) => {
  const { auth } = useAuth()
  const orgId = node.organization?.id ? `${parseId(node.organization.id)}` : undefined
  const userId = node.id ? `${parseId(node.id)}` : undefined

  const {
    config: orgConfig,
    loading: loadingOrgConfig,
    saving: savingOrgConfig,
    saveOrgConfig,
  } = useChannelConfig(orgId)
  const {
    config: userConfig,
    loading: loadingUserConfig,
    saving: savingUserConfig,
    saveUserConfig,
  } = useUserChannelConfig(userId)

  const [orgForm, setOrgForm] = useState<Partial<ChannelConfigData>>({})
  const [userForm, setUserForm] = useState<UserTelephonyForm>(EMPTY_USER_TELEPHONY_FORM)
  const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [isOrgDirty, setIsOrgDirty] = useState(false)
  const [isUserDirty, setIsUserDirty] = useState(false)

  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  useEffect(() => {
    if (!loadingOrgConfig) {
      setOrgForm(orgConfig)
      setIsOrgDirty(false)
    }
  }, [orgConfig, loadingOrgConfig])

  useEffect(() => {
    if (!loadingUserConfig) {
      setUserForm(buildUserTelephonyForm(userConfig))
      setIsUserDirty(false)
    }
  }, [userConfig, loadingUserConfig])

  useEffect(() => {
    let cancelled = false

    const loadCampaigns = async () => {
      const token = getToken()
      if (
        !token ||
        !orgConfig.vicidial_base_url ||
        !orgConfig.vicidial_api_user ||
        !orgConfig.vicidial_api_password
      ) {
        setCampaignOptions([])
        return
      }

      setLoadingCampaigns(true)
      try {
        const campaigns = await fetchVicidialCampaignOptions({
          token,
          baseUrl: orgConfig.vicidial_base_url,
          apiUser: orgConfig.vicidial_api_user,
          apiPassword: orgConfig.vicidial_api_password,
        })
        if (!cancelled) {
          setCampaignOptions(campaigns)
        }
      } catch {
        if (!cancelled) {
          setCampaignOptions([])
        }
      } finally {
        if (!cancelled) {
          setLoadingCampaigns(false)
        }
      }
    }

    loadCampaigns()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgConfig.vicidial_base_url,
    orgConfig.vicidial_api_user,
    orgConfig.vicidial_api_password,
  ])

  const handleOrgChange = (field: keyof ChannelConfigData, value: any) => {
    setOrgForm((prev) => ({ ...prev, [field]: value }))
    setIsOrgDirty(true)
  }

  const handleUserChange = (field: keyof UserTelephonyForm, value: string | boolean) => {
    setUserForm((prev) => ({ ...prev, [field]: value }))
    setIsUserDirty(true)
  }

  const handleSaveOrgConfig = async () => {
    const success = await saveOrgConfig(orgForm)
    if (success) {
      toast.success('Organization channels saved successfully')
      setIsOrgDirty(false)
      return
    }
    toast.error('Error saving organization channels')
  }

  const handleSaveUserConfig = async () => {
    const success = await saveUserConfig(userForm)
    if (success) {
      toast.success('User VICIdial mapping saved successfully')
      setIsUserDirty(false)
      return
    }
    toast.error('Error saving user VICIdial mapping')
  }

  const handleResetToInherited = () => {
    setUserForm(EMPTY_USER_TELEPHONY_FORM)
    setIsUserDirty(true)
  }

  if (!isAdmin) {
    return (
      <Card className='mb-5'>
        <Card.Body className='text-center py-10'>
          <i className='bi bi-lock-fill fs-1 text-muted mb-3 d-block' />
          <h5 className='text-gray-700'>Access Restricted</h5>
          <p className='text-muted mb-0'>
            Only administrators can manage channel and VICIdial settings.
          </p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className='d-flex flex-column gap-5'>
      <Card className='mb-0'>
        <Card.Body className='p-5'>
          <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-5'>
            <div>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <h3 className='fw-bold text-gray-900 mb-0'>
                  <i className='bi bi-broadcast me-2' />
                  Organization Channels
                </h3>
                <Badge bg='light' text='dark' className='border'>
                  {node.organization?.name || 'No organization'}
                </Badge>
              </div>
              <p className='text-muted fs-7 mb-0'>
                Shared SMS, Botmaker and SMTP settings for the organization.
              </p>
            </div>
            <Button
              variant='primary'
              onClick={handleSaveOrgConfig}
              disabled={savingOrgConfig || !isOrgDirty || !orgId}
            >
              {savingOrgConfig ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Saving...
                </>
              ) : (
                <>
                  <i className='bi bi-check-lg me-1' />
                  Save Channel Settings
                </>
              )}
            </Button>
          </div>

          {!orgId ? (
            <Card className='mb-0'>
              <Card.Body className='text-center py-10'>
                <i className='bi bi-exclamation-triangle fs-1 text-warning mb-3 d-block' />
                <h5 className='text-gray-700'>No Organization Assigned</h5>
                <p className='text-muted mb-0'>
                  This user must be assigned to an organization before editing SMS, Botmaker or
                  Email settings.
                </p>
              </Card.Body>
            </Card>
          ) : loadingOrgConfig ? (
            <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 280 }}>
              <Spinner animation='border' variant='primary' />
            </div>
          ) : (
            <>
              <Accordion alwaysOpen>
                <Accordion.Item eventKey='twilio'>
                  <Accordion.Header>
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-chat-dots fs-4 text-info' />
                      <span className='fw-bold fs-5'>Twilio SMS</span>
                      <Badge bg={orgForm.twilio_enabled ? 'success' : 'secondary'}>
                        {orgForm.twilio_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className='row g-4'>
                      <div className='col-12'>
                        <Form.Check
                          type='switch'
                          id='user_org_twilio_enabled'
                          label='Enable Twilio SMS'
                          checked={orgForm.twilio_enabled || false}
                          onChange={(e) => handleOrgChange('twilio_enabled', e.target.checked)}
                          className='fs-5'
                        />
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Account SID</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.twilio_account_sid || ''}
                            onChange={(e) => handleOrgChange('twilio_account_sid', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Auth Token</Form.Label>
                          <Form.Control
                            type='text'
                            autoComplete='off'
                            value={orgForm.twilio_auth_token || ''}
                            onChange={(e) => handleOrgChange('twilio_auth_token', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.twilio_phone_number || ''}
                            onChange={(e) => handleOrgChange('twilio_phone_number', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey='botmaker' className='mt-3'>
                  <Accordion.Header>
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-whatsapp fs-4 text-success' />
                      <span className='fw-bold fs-5'>Botmaker WhatsApp</span>
                      <Badge bg={orgForm.botmaker_enabled ? 'success' : 'secondary'}>
                        {orgForm.botmaker_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className='row g-4'>
                      <div className='col-12'>
                        <Form.Check
                          type='switch'
                          id='user_org_botmaker_enabled'
                          label='Enable Botmaker WhatsApp'
                          checked={orgForm.botmaker_enabled || false}
                          onChange={(e) => handleOrgChange('botmaker_enabled', e.target.checked)}
                          className='fs-5'
                        />
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>API URL</Form.Label>
                          <Form.Control
                            type='url'
                            value={orgForm.botmaker_api_url || ''}
                            onChange={(e) => handleOrgChange('botmaker_api_url', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Bot Name</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.botmaker_bot_name || ''}
                            onChange={(e) => handleOrgChange('botmaker_bot_name', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-12'>
                        <Form.Group>
                          <Form.Label>API Token (JWT)</Form.Label>
                          <Form.Control
                            as='textarea'
                            autoComplete='off'
                            rows={2}
                            value={orgForm.botmaker_api_token || ''}
                            onChange={(e) => handleOrgChange('botmaker_api_token', e.target.value)}
                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Default Channel ID</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.botmaker_default_channel_id || ''}
                            onChange={(e) => handleOrgChange('botmaker_default_channel_id', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Phone Line Number</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.botmaker_phone_line_number || ''}
                            onChange={(e) => handleOrgChange('botmaker_phone_line_number', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey='email' className='mt-3'>
                  <Accordion.Header>
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-envelope fs-4 text-warning' />
                      <span className='fw-bold fs-5'>Email SMTP</span>
                      <Badge bg={orgForm.email_enabled ? 'success' : 'secondary'}>
                        {orgForm.email_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className='row g-4'>
                      <div className='col-12'>
                        <Form.Check
                          type='switch'
                          id='user_org_email_enabled'
                          label='Enable Email'
                          checked={orgForm.email_enabled || false}
                          onChange={(e) => handleOrgChange('email_enabled', e.target.checked)}
                          className='fs-5'
                        />
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>SMTP Host</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.email_host || ''}
                            onChange={(e) => handleOrgChange('email_host', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-3'>
                        <Form.Group>
                          <Form.Label>Port</Form.Label>
                          <Form.Control
                            type='number'
                            value={orgForm.email_port || 587}
                            onChange={(e) => handleOrgChange('email_port', parseInt(e.target.value, 10) || 587)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-3'>
                        <Form.Group className='d-flex align-items-end h-100 pb-1'>
                          <Form.Check
                            type='switch'
                            id='user_org_email_use_tls'
                            label='Use TLS'
                            checked={orgForm.email_use_tls !== false}
                            onChange={(e) => handleOrgChange('email_use_tls', e.target.checked)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>SMTP User</Form.Label>
                          <Form.Control
                            type='text'
                            value={orgForm.email_host_user || ''}
                            onChange={(e) => handleOrgChange('email_host_user', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>SMTP Password</Form.Label>
                          <Form.Control
                            type='text'
                            autoComplete='off'
                            value={orgForm.email_host_password || ''}
                            onChange={(e) => handleOrgChange('email_host_password', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>From Email</Form.Label>
                          <Form.Control
                            type='email'
                            value={orgForm.email_from || ''}
                            onChange={(e) => handleOrgChange('email_from', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Reply-To Email</Form.Label>
                          <Form.Control
                            type='email'
                            value={orgForm.email_reply_to || ''}
                            onChange={(e) => handleOrgChange('email_reply_to', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey='vicidial-mapping' className='mt-3'>
          <Accordion.Header>
            <div className='d-flex align-items-center gap-2'>
              <img src='/media/tfi/vicidial-icon.svg' alt='VICIdial' style={{ width: '24px', height: '24px' }} />
              <span className='fw-bold fs-5'>VICIdial Settings</span>
              <Badge bg={userForm.vicidial_enabled ? 'success' : 'secondary'}>
                {userForm.vicidial_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-5'>
              <div>
                <p className='text-muted fs-7 mb-0'>
                  Separate telephony override for this user. Leave it empty to inherit the organization configuration.
                </p>
              </div>
              <div className='d-flex gap-2'>
                <Button variant='light' onClick={handleResetToInherited} disabled={savingUserConfig}>
                  Inherit Organization
                </Button>
                <Button
                  variant='primary'
                  onClick={handleSaveUserConfig}
                  disabled={savingUserConfig || !isUserDirty || !userId}
                >
                  {savingUserConfig ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-check-lg me-1' />
                      Save VICIdial Mapping
                    </>
                  )}
                </Button>
              </div>
            </div>

            {!userId ? (
              <Alert variant='warning' className='mb-0'>
                Save the user first before configuring the VICIdial mapping.
              </Alert>
            ) : loadingUserConfig ? (
              <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 220 }}>
                <Spinner animation='border' variant='primary' />
              </div>
            ) : (
              <>
                <div className='mt-4'>
                  <h6 className='fw-bold text-primary mb-3'><i className='bi bi-sliders me-2' />Override Mode</h6>
                  <div className='row g-4 mb-5'>
                    <div className='col-md-4'>
                      <Form.Group>
                        <Form.Label>Telephony provider</Form.Label>
                        <Form.Select
                          value={userForm.telephony_provider}
                          onChange={(e) =>
                            handleUserChange('telephony_provider', e.target.value as TelephonyProviderValue)
                          }
                        >
                          <option value=''>Inherit organization</option>
                          <option value='RINGCENTRAL'>RingCentral</option>
                          <option value='VICIDIAL'>VICIdial</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <div className='col-md-4'>
                      <Form.Group className='h-100 d-flex align-items-end'>
                        <Form.Check
                          type='switch'
                          id='user_vicidial_enabled'
                          label='Enable VICIdial override for this user'
                          checked={userForm.vicidial_enabled}
                          onChange={(e) => handleUserChange('vicidial_enabled', e.target.checked)}
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-4'>
                      <div className='alert alert-light-primary border border-primary border-dashed mb-0 h-100'>
                        <div className='fw-bold mb-1'>Current mode</div>
                        <div className='text-muted fs-7'>
                          {userForm.telephony_provider
                            ? `This user will explicitly use ${userForm.telephony_provider}.`
                            : 'This user will inherit the provider selected at organization level.'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <h6 className='fw-bold text-info mb-3'><i className='bi bi-person-badge me-2' />Agent Credentials</h6>
                  <div className='row g-4 mb-5'>
                    <div className='col-md-6'>
                      <Form.Group>
                        <Form.Label>VICIdial agent user</Form.Label>
                        <Form.Control
                          type='text'
                          value={userForm.vicidial_agent_user}
                          onChange={(e) => handleUserChange('vicidial_agent_user', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group>
                        <Form.Label>Agent password</Form.Label>
                        <Form.Control
                          type='password'
                          autoComplete='new-password'
                          value={userForm.vicidial_agent_password}
                          onChange={(e) => handleUserChange('vicidial_agent_password', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group>
                        <Form.Label>Extension / phone login</Form.Label>
                        <Form.Control
                          type='text'
                          value={userForm.vicidial_phone_login}
                          onChange={(e) => handleUserChange('vicidial_phone_login', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <Form.Group>
                        <Form.Label>Phone password</Form.Label>
                        <Form.Control
                          type='password'
                          autoComplete='new-password'
                          value={userForm.vicidial_phone_password}
                          onChange={(e) => handleUserChange('vicidial_phone_password', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className='d-flex align-items-center gap-2 mb-3'>
                    <h6 className='fw-bold text-success mb-0'><i className='bi bi-diagram-3 me-2' />Campaign Routing</h6>
                    {loadingCampaigns && (
                      <span className='text-primary fs-8 d-flex align-items-center gap-1'>
                        <Spinner animation='border' size='sm' />
                        Loading...
                      </span>
                    )}
                  </div>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <Form.Group>
                        <Form.Label>Campaign</Form.Label>
                        {campaignOptions.length > 0 ? (
                          <Form.Select
                            value={userForm.vicidial_campaign_id}
                            onChange={(e) => handleUserChange('vicidial_campaign_id', e.target.value)}
                          >
                            <option value=''>
                              Inherit organization
                              {orgConfig.vicidial_campaign_id
                                ? ` (${orgConfig.vicidial_campaign_id})`
                                : ' (no default campaign)'}
                            </option>
                            {campaignOptions.map((campaign) => (
                              <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                {campaign.campaign_id} - {campaign.campaign_name}
                                {campaign.active === 'Y' ? '' : ' (Inactive)'}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type='text'
                            placeholder={orgConfig.vicidial_campaign_id || 'Inherit organization campaign'}
                            value={userForm.vicidial_campaign_id}
                            onChange={(e) => handleUserChange('vicidial_campaign_id', e.target.value)}
                          />
                        )}
                        <Form.Text className='text-muted'>
                          Leave it blank to inherit the organization campaign.
                        </Form.Text>
                      </Form.Group>
                    </div>
                    <div className='col-md-6'>
                      <div className='alert alert-light-success border border-success border-dashed mb-0 h-100'>
                        <div className='fw-bold mb-1'>Routing behavior</div>
                        <div className='text-muted fs-7'>
                          Only set a campaign here when this agent should work on a different VICIdial queue.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
